import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import EventComponent from './EventComponent';
import EventsComponent from './EventsComponent';
import CreateEventNew from '../screens/CreateEventNew';
import { useAuth } from '../contexts/auth-context';
import { fetchEventsByOrganizer } from '../apiEndpoints';
import { useUserRoles, isUsherOnly } from '../utils/roleUtils';
import { useOrganization } from '@/contexts/organization-context';


interface MyEventsTabProps {
    eid?: string;
    activeTab?: string;
}

export default function MyEventsTab({ eid, activeTab }: MyEventsTabProps) {
    const { user, isLoading: isAuthLoading } = useAuth();
    const navigate = useNavigate();
    const [viewEventIdx, setViewEventIdx] = useState<number | null>(null);

    // Get user roles to check if user is usher only
    const { data: userRoles = [] } = useUserRoles();
    const isUsher = isUsherOnly(userRoles);

    const { userOwnOrganizer, selectedOrganizerId } = useOrganization();
    console.log('selectedOrganizerId', selectedOrganizerId);
    // Fetch user events from backend
    const {
        data: events = [],
        isFetching: isEventsLoading,
        isError: isEventsError,
    } = useQuery({
        queryKey: ['userEvents', user?.userId],
        queryFn: async () => {
            if (!user?.userId) return [];
            return await fetchEventsByOrganizer(user.userId);
        },
        enabled: !!user?.userId && !!selectedOrganizerId,
    });

    // Filter out promoted events for usher role and only show published events (not drafts)
    const filteredEvents = events?.data ? events.data.filter((event: any) => {
        // Only show published events (exclude drafts)
        if (event.status !== 'published') {
            return false;
        }
        // Hide promoted events from usher
        if (isUsher && event.eventType === 'promoted') {
            return false;
        }
        return true; // Show all other published events
    }) : [];

    // Use eid from URL params
    const eventId = eid !== 'create' ? eid : undefined;

    useEffect(() => {
        if (eventId) {
            setViewEventIdx(null);
            //navigate('/organizations/events');
        }
    }, [selectedOrganizerId]);

    // When navigating back to the events list (e.g. clicking "Events" in the header),
    // the URL's eid clears but a stale viewEventIdx from a prior list click would keep
    // rendering the old event's details instead of the events list. Reset it here.
    useEffect(() => {
        if (!eventId) {
            setViewEventIdx(null);
        }
    }, [eventId]);

    if (isAuthLoading) return <div className="py-16 text-center text-base text-grey40 dark:text-nightMuted">Loading...</div>;

    if (eid === 'create') {
        return <CreateEventNew embedded />;
    }

    if (eventId) {
        return <EventComponent eventId={eventId} activeTab={activeTab} />;
    }

    return (
        viewEventIdx === null ? (
            <EventsComponent events={filteredEvents} setViewEventIdx={setViewEventIdx} isEventsLoading={isEventsLoading} isEventsError={isEventsError} navigate={navigate} />
        ) : (
            filteredEvents[viewEventIdx]?._id && (
                <EventComponent eventId={filteredEvents[viewEventIdx]?._id} activeTab={activeTab} />
            )
        )
    );
}